import type { ApiErrorCode, ApiErrorData, ApiValidationErrors } from '../types/api/responses'

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value)
}

function normalizeMessageList(value: unknown): string[] {
  if (Array.isArray(value)) {
    return value
      .flatMap((item) => normalizeMessageList(item))
      .filter((message) => message.length > 0)
  }

  if (typeof value === 'string') {
    const message = value.trim()
    return message ? [message] : []
  }

  if (typeof value === 'number' || typeof value === 'boolean') {
    return [String(value)]
  }

  return []
}

function pickNestedErrorData(payload: unknown): Record<string, unknown> | undefined {
  if (!isRecord(payload)) {
    return undefined
  }

  if (isRecord(payload.data)) {
    return payload.data
  }

  return payload
}

export function normalizeApiValidationErrors(value: unknown): ApiValidationErrors {
  if (!isRecord(value)) {
    return {}
  }

  return Object.entries(value).reduce<ApiValidationErrors>((errors, [field, messages]) => {
    const normalizedMessages = normalizeMessageList(messages)
    if (normalizedMessages.length > 0) {
      errors[field] = normalizedMessages
    }
    return errors
  }, {})
}

export function extractApiValidationErrors(payload: unknown): ApiValidationErrors {
  if (!isRecord(payload)) {
    return {}
  }

  const nestedData = pickNestedErrorData(payload)
  return normalizeApiValidationErrors(nestedData?.errors ?? payload.errors)
}

export function getFirstApiValidationMessage(errors: ApiValidationErrors): string {
  for (const messages of Object.values(errors)) {
    const firstMessage = messages.find((message) => message.trim().length > 0)
    if (firstMessage) {
      return firstMessage
    }
  }

  return ''
}

export function extractApiErrorCode(payload: unknown): ApiErrorCode | string | undefined {
  if (!isRecord(payload)) {
    return undefined
  }

  const nestedData = pickNestedErrorData(payload)
  const errorCode = nestedData?.errorCode ?? payload.errorCode

  return typeof errorCode === 'string' && errorCode.trim() ? errorCode : undefined
}

export function extractApiErrorData(payload: unknown): ApiErrorData | undefined {
  if (!isRecord(payload)) {
    return undefined
  }

  const nestedData = pickNestedErrorData(payload)
  const errors = extractApiValidationErrors(payload)
  const errorCode = extractApiErrorCode(payload)

  if (!nestedData && Object.keys(errors).length === 0 && !errorCode) {
    return undefined
  }

  return {
    ...(nestedData ?? {}),
    ...(errorCode ? { errorCode } : {}),
    ...(Object.keys(errors).length > 0 ? { errors } : {}),
  }
}

export function extractApiErrorMessage(payload: unknown, fallback = ''): string {
  if (typeof payload === 'string') {
    return payload.trim() || fallback
  }

  if (!isRecord(payload)) {
    return fallback
  }

  const validationMessage = getFirstApiValidationMessage(extractApiValidationErrors(payload))
  if (validationMessage) {
    return validationMessage
  }

  const message = payload.message ?? payload.error
  if (typeof message === 'string' && message.trim()) {
    return message
  }

  return fallback
}
